/**
 * Shared model formatting + pricing helpers for the status bars
 * (components/chat/SessionStatusBar.tsx and components/layout/StatusBar.tsx).
 * Single source of truth — the two bars previously carried verbatim copies
 * that drifted (only one knew how to format `claude-fable-5`, and both priced
 * every model at legacy Opus rates).
 */

export interface UsageTotals {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

interface ModelRates {
  match: RegExp;
  input: number;      // $ per MTok
  output: number;     // $ per MTok
  cacheRead: number;  // $ per MTok (~0.1× input)
  cacheWrite: number; // $ per MTok (~1.25× input — 5-minute cache TTL)
}

// Per-MTok pricing. Source: claude-api skill model catalog (cached 2026-05-26):
//   Fable 5    $10 in / $50 out
//   Opus 4.8   $5  in / $25 out
//   Sonnet 4.6 $3  in / $15 out
//   Haiku 4.5  $1  in / $5  out
// Cache read ≈ 0.1× input; 5-minute-TTL cache write ≈ 1.25× input (prompt
// caching economics from the same source). Matched by family substring so
// dated/suffixed ids (e.g. claude-sonnet-4-5-20250929) price correctly too.
const PRICING: ModelRates[] = [
  { match: /fable/i,  input: 10, output: 50, cacheRead: 1.0, cacheWrite: 12.5 },
  { match: /opus/i,   input: 5,  output: 25, cacheRead: 0.5, cacheWrite: 6.25 },
  { match: /sonnet/i, input: 3,  output: 15, cacheRead: 0.3, cacheWrite: 3.75 },
  { match: /haiku/i,  input: 1,  output: 5,  cacheRead: 0.1, cacheWrite: 1.25 },
];

// Fallback: any model not in the table is billed at Opus rates (safe upper
// bound for the families the gateway actually serves).
const OPUS_FALLBACK = PRICING[1];

/** Estimated $ cost of a usage total. `modelId` picks the rate row; unknown
 *  or missing model falls back to Opus rates. */
export function estimateCost(u: UsageTotals, modelId?: string | null): number {
  const r = (modelId && PRICING.find((p) => p.match.test(modelId))) || OPUS_FALLBACK;
  return (
    (u.input / 1e6) * r.input +
    (u.output / 1e6) * r.output +
    (u.cacheRead / 1e6) * r.cacheRead +
    (u.cacheWrite / 1e6) * r.cacheWrite
  );
}

/** "claude-opus-4-6" → "Opus-4.6" · "claude-fable-5" → "Fable 5" */
export function formatModel(id: string): string {
  const match = id.match(/claude-(\w+)-(\d+)-(\d+)/);
  if (match) return `${match[1].charAt(0).toUpperCase() + match[1].slice(1)}-${match[2]}.${match[3]}`;
  // single-number models, e.g. claude-fable-5 → "Fable 5"
  const single = id.match(/^claude-([a-z]+)-(\d+)$/);
  if (single) return `${single[1].charAt(0).toUpperCase() + single[1].slice(1)} ${single[2]}`;
  return id;
}

/** Family rank for model-picker sorting: Fable first, then Opus, Sonnet, rest. */
export function modelFamilyRank(id: string): number {
  return id.includes('fable') ? 0 : id.includes('opus') ? 1 : id.includes('sonnet') ? 2 : 3;
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(n);
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatReset(isoDate: string): string {
  const diffMs = new Date(isoDate).getTime() - Date.now();
  if (diffMs <= 0) return 'now';
  const diffH = Math.floor(diffMs / 3600000);
  const diffM = Math.floor((diffMs % 3600000) / 60000);
  if (diffH >= 24) return `${Math.floor(diffH / 24)}d${diffH % 24}h`;
  return diffH > 0 ? `${diffH}h${diffM}m` : `${diffM}m`;
}
